import { Inject } from '@wendellhu/redi';
import { IBackendLogService, type BackendLogService } from '@/backend/core';
import { ICaseRepository } from './interfaces/case.repository.interface';
import { CaseHomeCardMapper } from './mappers/case-home.mapper';
import type { CaseMaindataDTO } from '@holoview/dao-gen/generated';
import type {
  CaseAppendsQueryParams,
  CaseDetailDto,
  CaseEditableDto,
  CaseLibraryQueryParams,
  CaseListPageDto,
  CaseListQueryParams,
  CaseShowcaseCardDto,
  CaseShowcaseQueryParams,
} from './dto/case.dto';
import { CaseCreateMapper } from './mappers/case-create.mapper';
import { CaseLibraryMapper } from './mappers/case-library.mapper';
import { CaseDetailMapper } from './mappers/case-detail.mapper';
import { CaseEditMapper } from './mappers/case-edit.mapper';
import type { CaseCreateParams } from './dto/case-create.dto';

export class CaseService {
  private readonly log: BackendLogService;

  constructor(
    @Inject(ICaseRepository) private readonly repo: ICaseRepository,
    @IBackendLogService rootLogger: BackendLogService,
  ) {
    this.log = rootLogger.child('CaseService');
  }

  async findAll(filter: CaseListQueryParams): Promise<CaseListPageDto<CaseMaindataDTO>> {
    return this.repo.findAll(filter);
  }

  async searchLibraryCases(params: CaseLibraryQueryParams) {
    const result = await this.repo.searchLibraryCases(params);
    return {
      data: CaseLibraryMapper.toRowList(result.data),
      total: result.total,
    };
  }

  async findById(id: number): Promise<CaseMaindataDTO | null> {
    return this.repo.findById(id);
  }

  async createMainData(params: CaseCreateParams): Promise<CaseMaindataDTO> {
    const mainData = await this.repo.createMainData(CaseCreateMapper.toMainDataParams(params));
    if (!mainData.id) {
      throw new Error('创建案例成功但未返回 id');
    }
    await this.repo.createArchive(mainData.id, CaseCreateMapper.toArchiveCreateParams(params));
    return mainData;
  }

  async findEditableCase(id: number): Promise<CaseEditableDto> {
    const row = await this.repo.findEditableCase(id);
    const editable = CaseEditMapper.toEditable(row);
    if (!editable) throw new Error('案例不存在或无法编辑');
    return editable;
  }

  async updateEditableCase(id: number, params: CaseCreateParams): Promise<void> {
    await this.repo.updateMainData(id, CaseCreateMapper.toMainDataParams(params));
    await this.repo.updateArchive(id, CaseCreateMapper.toArchiveCreateParams(params));
  }

  async updateStatus(id: number, status: string): Promise<void> {
    this.log.debug('case.status.update.started', {
      context: { caseId: id, status },
    });
    await this.repo.updateStatus(id, status);
  }

  async searchWithAppends(params: CaseAppendsQueryParams): Promise<CaseMaindataDTO[]> {
    return this.repo.searchWithAppends(params);
  }

  async getShowcaseCaseCards(params: CaseShowcaseQueryParams): Promise<CaseShowcaseCardDto[]> {
    const rows = await this.repo.getShowcaseCaseCards(params);
    return CaseHomeCardMapper.toShowcaseCardList(rows);
  }

  async findCasesWithArchiveByIds(ids: number[]): Promise<Record<string, unknown>[]> {
    return this.repo.findCasesWithArchiveByIds(ids);
  }

  async findCaseDetailsByIds(ids: number[]): Promise<CaseDetailDto[]> {
    return CaseDetailMapper.toDetailList(await this.findCasesWithArchiveByIds(ids));
  }
}
